import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import Title from "../../../components/Admin/Title";
import useIsAdmin from "../../../hooks/useIsAdmin";
import Button from "../../../components/Button";
import ErrorPage from "../../ErrorPage";
import { useUser } from "../../../components/UserContext";
import { useParams } from "react-router-dom";
import LoadingContent from "../../../components/LoadingContent";
import NewsDiscord from "../../../components/Admin/SocialMedia/NewsDiscord";
import UploadRaceX from "../../../components/Admin/SocialMedia/UploadRaceX";
import UploadRaceFacebook from "../../../components/Admin/SocialMedia/UploadRaceFacebook";
import UploadRaceInstagram from "../../../components/Admin/SocialMedia/UploadRaceInstagram";
import NewsX from "../../../components/Admin/SocialMedia/NewsX";
import NewsFacebook from "../../../components/Admin/SocialMedia/NewsFacebook";
import NewsInstagram from "../../../components/Admin/SocialMedia/NewsInstagram";

const SocialMediaNews = () => {
  const { access } = useIsAdmin();
  document.title = "LVF1 - Social Media News";
  let { news_id, entries } = useParams();
  const { user } = useUser() as any;
  const token = window.localStorage.getItem("userLogged");
  const [logs, setLogs] = useState<string[]>([]);
  // Estados compartidos
  const [xEmbed, setXEmbed] = useState(``);
  const [instagramEmbed, setInstagramEmbed] = useState(``);
  const [facebookEmbed, setFacebookEmbed] = useState(``);
  const [discordTitle, setDiscordTitle] = useState(``);
  const [discordEmbed, setDiscordEmbed] = useState(``);

  const [data, setData] = useState<any>();
  const [validated, setValidated] = useState(false);

  // Estados de carga independientes y global
  const [isSubmittingAll, setIsSubmittingAll] = useState(false);
  const [loadingFB, setLoadingFB] = useState(false);
  const [loadingX, setLoadingX] = useState(false);
  const [loadingDiscord, setLoadingDiscord] = useState(false);
  const [loadingInstagram, setLoadingInstagram] = useState(false);

  const BASE_URL =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5500"
      : "https://ligavirtualf1.es:5500";

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/grandprix/getSocialMediaNewsInfo`, {
        params: { news_id, entries },
      })
      .then(({ data }) => {
        setData(data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }, [news_id, BASE_URL]);

  // Auxiliar para empaquetar FormData de manera idéntica
  const createFormData = (
    key: string,
    value: string,
    extraKey?: string,
    extraValue?: string,
    extraKey2?: string,
    extraValue2?: string,
    extraKey3?: string,
    extraValue3?: string,
  ) => {
    if (!token || !news_id) return null;
    const formData = new FormData();
    formData.append(key, value);
    if (extraKey && extraValue) formData.append(extraKey, extraValue);
    if (extraKey2 && extraValue2) formData.append(extraKey2, extraValue2);
    if (extraKey3 && extraValue3) formData.append(extraKey3, extraValue3);
    formData.append("news_id", news_id);
    formData.append("user", user);
    formData.append("token", JSON.parse(token).token);
    return formData;
  };

  // --- ENVIOS INDIVIDUALES ---
  const submitFacebook = async () => {
    if (!facebookEmbed.trim())
      return toast.error("El campo de Facebook está vacío");
    setLoadingFB(true);
    try {
      const body = createFormData(
        "facebook_embed",
        facebookEmbed,
        "type",
        data.news.grand_prix_event_id != null ? "imagesnew" : "imagenew",
      );
      await axios.post(`${BASE_URL}/api/admin/publishFacebookNews`, body);
      toast.success("Facebook post created successfully");
    } catch (err) {
      toast.error("Error posting to Facebook");
    } finally {
      setLoadingFB(false);
    }
  };

  const submitX = async () => {
    if (!xEmbed.trim()) return toast.error("El campo de X está vacío");
    setLoadingX(true);
    try {
      const body = createFormData(
        "x_embed",
        xEmbed,
        "type",
        data.news.grand_prix_event_id != null ? "imagesnew" : "imagenew",
      );
      await axios.post(`${BASE_URL}/api/admin/publishXNews`, body);
      toast.success("X post created successfully");
    } catch (err) {
      toast.error("Error posting to X");
    } finally {
      setLoadingX(false);
    }
  };

  const submitDiscord = async () => {
    if (!discordEmbed.trim())
      return toast.error("El campo de Discord está vacío");
    setLoadingDiscord(true);
    try {
      const body = createFormData(
        "discord_embed",
        discordEmbed,
        "discord_title",
        discordTitle,
        "type_championship",
        data.type_championship,
        "show_image",
        data.news.grand_prix_event_id != null ? "noimage" : "image",
      );
      await axios.post(`${BASE_URL}/api/admin/publishDiscordNews`, body);
      toast.success("Discord message created successfully");
    } catch (err) {
      toast.error("Error posting to Discord");
    } finally {
      setLoadingDiscord(false);
    }
  };

  const submitInstagram = async () => {
    if (!instagramEmbed.trim())
      return toast.error("El campo de Instagram está vacío");
    setLoadingInstagram(true);
    try {
      // 1. Creamos el FormData base con nuestro ayudante
      const body = createFormData(
        "instagram_embed",
        instagramEmbed,
        "type",
        data.news.grand_prix_event_id != null ? "imagesnew" : "imagenew",
      );

      await axios.post(`${BASE_URL}/api/admin/publishInstagramNews`, body);
      toast.success("Instagram post created successfully");
    } catch (err) {
      toast.error("Error posting to Instagram");
    } finally {
      setLoadingInstagram(false);
    }
  };

  // --- ENVÍO DE TODO EN LOTE ---
  const handleSubmitAll = async (event: React.FormEvent<HTMLFormElement>) => {
    if (token == null) return;
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      toast.error("Por favor, rellena todos los campos requeridos (*)");
      return;
    }

    setValidated(true);
    setIsSubmittingAll(true);
    setLogs([]); // Limpiamos logs anteriores si los hubiera

    // Helper interno para añadir líneas al log de forma limpia
    const addLog = (message: string) => {
      setLogs((prev) => [
        ...prev,
        `[${new Date().toLocaleTimeString()}] ${message}`,
      ]);
    };

    addLog("🚀 Iniciando publicación simultánea en todas las redes...");

    // Preparamos FormDatas
    const instagramBatchData = createFormData(
      "instagram_embed",
      instagramEmbed,
      "type",
      data.news.grand_prix_event_id != null ? "imagesnew" : "imagenew",
    );

    const discordBatchData = createFormData(
      "discord_embed",
      discordEmbed,
      "discord_title",
      discordTitle,
      "type_championship",
      data.type_championship,
      "show_image",
      data.news.grand_prix_event_id != null ? "noimage" : "image",
    );

    // Envolvemos cada petición en su propio mini-ciclo para capturar el progreso real
    const facebookPromise = axios
      .post(
        `${BASE_URL}/api/admin/publishFacebookNews`,
        createFormData(
          "facebook_embed",
          facebookEmbed,
          "type",
          data.news.grand_prix_event_id != null ? "imagesnew" : "imagenew",
        ),
      )
      .then(() => addLog("✅ Facebook: Posted successfully."))
      .catch(() => addLog("❌ Facebook: Error in the post."));

    const xPromise = axios
      .post(
        `${BASE_URL}/api/admin/publishXNews`, //same than news
        createFormData(
          "x_embed",
          xEmbed,
          "type",
          data.news.grand_prix_event_id != null ? "imagesnew" : "imagenew",
        ),
      )
      .then(() => addLog("✅ X (Twitter): Posted successfully."))
      .catch(() => addLog("❌ X (Twitter): Error in the post."));

    const discordPromise = axios
      .post(`${BASE_URL}/api/admin/publishDiscordNews`, discordBatchData)
      .then(() => addLog("✅ Discord: Message sent successfully."))
      .catch(() => addLog("❌ Discord: Shipping error."));

    const instagramPromise = axios
      .post(`${BASE_URL}/api/admin/publishInstagramNews`, instagramBatchData)
      .then(() => addLog("✅ Instagram: Posted successfully."))
      .catch(() => addLog("❌ Instagram: Error en la publicación."));

    try {
      // Esperamos a que terminen todas sus flujos individuales
      await Promise.all([
        facebookPromise,
        xPromise,
        discordPromise,
        instagramPromise,
      ]);
      addLog("🏁 Batch processing completed.");
      toast.success("Batch operation completed! Check the log for details.");
      setValidated(false);
    } catch (error) {
      toast.error("A critical error occurred while processing the batch.");
    } finally {
      setIsSubmittingAll(false);
    }
  };

  if (!access || news_id == null) return <ErrorPage />;
  if (data == null) return <LoadingContent />;

  return (
    <div className="full-height padding-md app-container flex-center-top page-transition">
      <div className="form-container">
        <Title text="Upload Social Media News" />
        <small className="mandatory-fields">(*) Required fields</small>

        <Form noValidate validated={validated} onSubmit={handleSubmitAll}>
          <NewsDiscord
            data={data}
            discordTitle={discordTitle}
            setDiscordTitle={setDiscordTitle}
            discordEmbed={discordEmbed}
            setDiscordEmbed={setDiscordEmbed}
            onIndividualSubmit={submitDiscord}
            uploading={loadingDiscord}
          />

          <NewsX
            data={data}
            xEmbed={xEmbed}
            setXEmbed={setXEmbed}
            onIndividualSubmit={submitX}
            uploading={loadingX}
          />

          <NewsFacebook
            data={data}
            facebookEmbed={facebookEmbed}
            setFacebookEmbed={setFacebookEmbed}
            onIndividualSubmit={submitFacebook}
            uploading={loadingFB}
          />

          <NewsInstagram
            data={data}
            instagramEmbed={instagramEmbed}
            setInstagramEmbed={setInstagramEmbed}
            onIndividualSubmit={submitInstagram}
            uploading={loadingInstagram}
          />
          {/* Consola de logs en vivo */}
          {logs.length > 0 && (
            <div id="deployment-log" className="mt-4 p-3 rounded text-start">
              <div>⚙️ Live Deployment Log:</div>
              {logs.map((log, index) => (
                <div key={index} className="mb-1">
                  {log}
                </div>
              ))}
            </div>
          )}
          {/* Botón Maestro Global */}
          <div className="text-center mt-4 mb-5">
            <Button
              text={
                isSubmittingAll
                  ? "Posting everything..."
                  : "Share on all social media platforms"
              }
              type="submit"
              disabled={isSubmittingAll}
              icon={isSubmittingAll ? faSpinner : faPaperPlane}
            />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default SocialMediaNews;
